/**
 * Enterprise-Grade HIPAA-Compliant Audit Log Service
 * 
 * This service provides comprehensive audit logging for healthcare environments.
 * It integrates with Cloud Functions for secure server-side logging.
 * 
 * HIPAA Requirements Addressed:
 * - 164.312(b) - Audit controls
 * - 164.308(a)(1)(ii)(D) - Information system activity review
 * - 164.312(c)(1) - Integrity controls
 * - 164.312(d) - Person or entity authentication
 */

import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  limit as firestoreLimit,
  startAfter,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/config/firebase';
import {
  AuditLog,
  AuditLogAction,
  AuditLogSeverity,
  AuditLogFilter,
  CreateAuditLogInput,
  AuditStatistics,
  AuditSummaryByUser,
  PHIAccessReport,
  HIPAAComplianceReport,
  AuditEntityType,
  PHIDataType,
  AccessReason,
} from '@/models/audit-log.model';

const AUDIT_LOGS_COLLECTION = 'auditLogs';
const functions = getFunctions();

// ============================================================================
// SESSION & CONTEXT MANAGEMENT
// ============================================================================

interface AuditContext {
  sessionId?: string;
  requestId?: string;
  ipAddress?: string;
  userAgent?: string;
}

let currentContext: AuditContext = {};

/**
 * Set the current audit context (call on login/session start)
 */
export function setAuditContext(context: Partial<AuditContext>): void {
  currentContext = { ...currentContext, ...context };
}

/**
 * Clear the audit context (call on logout)
 */
export function clearAuditContext(): void {
  currentContext = {};
}

/**
 * Generate a unique request ID for correlation
 */
function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

// ============================================================================
// AUDIT LOG SERVICE
// ============================================================================

export class AuditLogService {
  // ==========================================================================
  // WRITE OPERATIONS (via Cloud Functions)
  // ==========================================================================

  /**
   * Write an audit log entry (via Cloud Function for security)
   */
  static async writeLog(input: CreateAuditLogInput): Promise<{ success: boolean; logId?: string }> {
    try {
      const writeAuditLog = httpsCallable<CreateAuditLogInput, { success: boolean; logId: string; hash: string }>(
        functions,
        'writeAuditLog'
      );

      const enrichedInput: CreateAuditLogInput = {
        ...input,
        sessionId: input.sessionId || currentContext.sessionId,
        requestId: input.requestId || currentContext.requestId || generateRequestId(),
      };

      const result = await writeAuditLog(enrichedInput);
      return { success: result.data.success, logId: result.data.logId };
    } catch (error) {
      console.error('Error writing audit log:', error);
      // Fallback: log error but don't fail the operation
      return { success: false };
    }
  }

  /**
   * Write multiple audit log entries in batch
   */
  static async writeLogBatch(logs: CreateAuditLogInput[]): Promise<{ success: boolean; count: number }> {
    try {
      const writeAuditLogBatch = httpsCallable<
        { logs: CreateAuditLogInput[] },
        { success: boolean; logIds: string[]; count: number }
      >(functions, 'writeAuditLogBatch');

      const enrichedLogs = logs.map(log => ({
        ...log,
        sessionId: log.sessionId || currentContext.sessionId,
        requestId: log.requestId || currentContext.requestId || generateRequestId(),
      }));

      const result = await writeAuditLogBatch({ logs: enrichedLogs });
      return { success: result.data.success, count: result.data.count };
    } catch (error) {
      console.error('Error writing batch audit logs:', error);
      return { success: false, count: 0 };
    }
  }

  // ==========================================================================
  // CONVENIENCE WRITE METHODS
  // ==========================================================================

  /**
   * Log a user login event
   */
  static async logLogin(
    userId: string,
    userEmail: string,
    userName?: string,
    success: boolean = true,
    errorMessage?: string
  ): Promise<void> {
    await this.writeLog({
      action: success ? AuditLogAction.userLogin : AuditLogAction.userLoginFailed,
      severity: success ? AuditLogSeverity.info : AuditLogSeverity.warning,
      description: success 
        ? `User logged in: ${userEmail}`
        : `Login failed for: ${userEmail}`,
      userId,
      userEmail,
      userName,
      entityType: AuditEntityType.session,
      success,
      errorMessage,
    });
  }

  /**
   * Log a user logout event
   */
  static async logLogout(userId: string, userEmail: string): Promise<void> {
    await this.writeLog({
      action: AuditLogAction.userLogout,
      severity: AuditLogSeverity.info,
      description: `User logged out: ${userEmail}`,
      userId,
      userEmail,
      entityType: AuditEntityType.session,
      success: true,
    });
  }

  /**
   * Log PHI access with required justification
   */
  static async logPHIAccess(
    entityType: AuditEntityType,
    entityId: string,
    entityName: string,
    accessReason: AccessReason,
    phiDataTypes: PHIDataType[],
    accessJustification?: string,
    patientId?: string
  ): Promise<void> {
    await this.writeLog({
      action: AuditLogAction.phiAccessed,
      severity: accessReason === AccessReason.emergencyAccess 
        ? AuditLogSeverity.warning 
        : AuditLogSeverity.info,
      description: `PHI accessed: ${entityName} (${entityType})`,
      entityType,
      entityId,
      entityName,
      containsPHI: true,
      phiDataTypes,
      accessReason,
      accessJustification,
      patientId,
      success: true,
    });
  }

  /**
   * Log data export (especially important for PHI)
   */
  static async logDataExport(
    entityType: string,
    recordCount: number,
    containsPHI: boolean,
    exportFormat: string,
    accessReason?: AccessReason
  ): Promise<void> {
    await this.writeLog({
      action: containsPHI ? AuditLogAction.phiExported : AuditLogAction.dataExported,
      severity: containsPHI ? AuditLogSeverity.warning : AuditLogSeverity.info,
      description: `Data exported: ${recordCount} ${entityType} records (${exportFormat})`,
      entityType,
      containsPHI,
      accessReason: containsPHI ? (accessReason || AccessReason.operations) : undefined,
      details: {
        recordCount,
        exportFormat,
      },
      success: true,
    });
  }

  /**
   * Log unauthorized access attempt
   */
  static async logUnauthorizedAccess(
    attemptedResource: string,
    userId?: string,
    userEmail?: string,
    details?: Record<string, unknown>
  ): Promise<void> {
    await this.writeLog({
      action: AuditLogAction.unauthorizedAccess,
      severity: AuditLogSeverity.critical,
      description: `Unauthorized access attempt: ${attemptedResource}`,
      userId,
      userEmail,
      entityType: AuditEntityType.system,
      entityName: attemptedResource,
      success: false,
      details,
    });
  }

  /**
   * Log a generic entity operation
   */
  static async logEntityOperation(
    action: AuditLogAction,
    entityType: AuditEntityType | string,
    entityId: string,
    entityName: string,
    description: string,
    options?: {
      containsPHI?: boolean;
      phiDataTypes?: PHIDataType[];
      accessReason?: AccessReason;
      changeDetails?: {
        fieldsChanged?: string[];
        beforeValues?: Record<string, unknown>;
        afterValues?: Record<string, unknown>;
        changeType: 'create' | 'update' | 'delete' | 'access' | 'other';
      };
      details?: Record<string, unknown>;
      success?: boolean;
      errorMessage?: string;
    }
  ): Promise<void> {
    await this.writeLog({
      action,
      description,
      entityType,
      entityId,
      entityName,
      ...options,
    });
  }

  // ==========================================================================
  // READ OPERATIONS
  // ==========================================================================

  /**
   * Get audit logs with comprehensive filtering
   */
  static async getAuditLogs(
    filter: AuditLogFilter = {},
    limitCount: number = 100,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    lastDoc?: QueryDocumentSnapshot<any>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<{ logs: AuditLog[]; lastDocument?: QueryDocumentSnapshot<any> }> {
    let q: any = query(
      collection(db, AUDIT_LOGS_COLLECTION),
      orderBy('timestamp', 'desc')
    );

    // Apply filters - Firestore has compound query limitations
    // We'll apply the most specific filters first
    if (filter.action) {
      q = query(q, where('action', '==', filter.action));
    }
    if (filter.actions && filter.actions.length > 0 && !filter.action) {
      q = query(q, where('action', 'in', filter.actions.slice(0, 10))); // Firestore limit
    }
    if (filter.severity) {
      q = query(q, where('severity', '==', filter.severity));
    }
    if (filter.userId) {
      q = query(q, where('userId', '==', filter.userId));
    }
    if (filter.entityType) {
      q = query(q, where('entityType', '==', filter.entityType));
    }
    if (filter.entityId) {
      q = query(q, where('entityId', '==', filter.entityId));
    }
    if (filter.containsPHI !== undefined) {
      q = query(q, where('containsPHI', '==', filter.containsPHI));
    }
    if (filter.success !== undefined) {
      q = query(q, where('success', '==', filter.success));
    }

    // Date range filters
    if (filter.startDate) {
      q = query(q, where('timestamp', '>=', Timestamp.fromDate(filter.startDate)));
    }
    if (filter.endDate) {
      q = query(q, where('timestamp', '<=', Timestamp.fromDate(filter.endDate)));
    }

    // Pagination
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    q = query(q, firestoreLimit(limitCount));

    const snapshot = await getDocs(q);
    let logs = snapshot.docs.map(docSnap => this.docToAuditLog(docSnap.id, docSnap.data()));

    // Apply client-side filters for fields that can't be combined in Firestore
    if (filter.searchQuery) {
      const searchLower = filter.searchQuery.toLowerCase();
      logs = logs.filter(log =>
        log.description.toLowerCase().includes(searchLower) ||
        log.userName?.toLowerCase().includes(searchLower) ||
        log.userEmail?.toLowerCase().includes(searchLower) ||
        log.entityName?.toLowerCase().includes(searchLower)
      );
    }

    if (filter.accessReason) {
      logs = logs.filter(log => log.accessReason === filter.accessReason);
    }

    if (filter.patientId) {
      logs = logs.filter(log => log.patientId === filter.patientId);
    }

    const lastDocument = snapshot.docs.length > 0 
      ? snapshot.docs[snapshot.docs.length - 1] 
      : undefined;

    return { logs, lastDocument };
  }

  /**
   * Get audit logs for a specific entity
   */
  static async getEntityAuditLogs(
    entityType: string,
    entityId: string,
    limitCount: number = 50
  ): Promise<AuditLog[]> {
    const q = query(
      collection(db, AUDIT_LOGS_COLLECTION),
      where('entityType', '==', entityType),
      where('entityId', '==', entityId),
      orderBy('timestamp', 'desc'),
      firestoreLimit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => this.docToAuditLog(docSnap.id, docSnap.data()));
  }

  /**
   * Get audit logs for a specific user
   */
  static async getUserAuditLogs(
    userId: string,
    limitCount: number = 50
  ): Promise<AuditLog[]> {
    const q = query(
      collection(db, AUDIT_LOGS_COLLECTION),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      firestoreLimit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => this.docToAuditLog(docSnap.id, docSnap.data()));
  }

  /**
   * Get PHI access logs (HIPAA requirement)
   */
  static async getPHIAccessLogs(
    limitCount: number = 100,
    filter?: {
      patientId?: string;
      startDate?: Date;
      endDate?: Date;
    }
  ): Promise<AuditLog[]> {
    let q: any = query(
      collection(db, AUDIT_LOGS_COLLECTION),
      where('containsPHI', '==', true),
      orderBy('timestamp', 'desc'),
      firestoreLimit(limitCount)
    );

    if (filter?.startDate) {
      q = query(q, where('timestamp', '>=', Timestamp.fromDate(filter.startDate)));
    }
    if (filter?.endDate) {
      q = query(q, where('timestamp', '<=', Timestamp.fromDate(filter.endDate)));
    }

    const snapshot = await getDocs(q);
    let logs = snapshot.docs.map(docSnap => this.docToAuditLog(docSnap.id, docSnap.data()));

    if (filter?.patientId) {
      logs = logs.filter(log => log.patientId === filter.patientId);
    }

    return logs;
  }

  /**
   * Get security events (unauthorized access, suspicious activity, etc.)
   */
  static async getSecurityEvents(limitCount: number = 100): Promise<AuditLog[]> {
    const securityActions = [
      AuditLogAction.unauthorizedAccess,
      AuditLogAction.permissionDenied,
      AuditLogAction.suspiciousActivity,
      AuditLogAction.securityAlertTriggered,
      AuditLogAction.brute_force_detected,
      AuditLogAction.userLoginFailed,
      AuditLogAction.accountLocked,
    ];

    const q = query(
      collection(db, AUDIT_LOGS_COLLECTION),
      where('action', 'in', securityActions),
      orderBy('timestamp', 'desc'),
      firestoreLimit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => this.docToAuditLog(docSnap.id, docSnap.data()));
  }

  /**
   * Get emergency access logs (Break-the-Glass)
   */
  static async getEmergencyAccessLogs(limitCount: number = 100): Promise<AuditLog[]> {
    const q = query(
      collection(db, AUDIT_LOGS_COLLECTION),
      where('accessReason', '==', AccessReason.emergencyAccess),
      orderBy('timestamp', 'desc'),
      firestoreLimit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => this.docToAuditLog(docSnap.id, docSnap.data()));
  }

  /**
   * Get recent audit logs
   */
  static async getRecentAuditLogs(limitCount: number = 50): Promise<AuditLog[]> {
    const q = query(
      collection(db, AUDIT_LOGS_COLLECTION),
      orderBy('timestamp', 'desc'),
      firestoreLimit(limitCount)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => this.docToAuditLog(docSnap.id, docSnap.data()));
  }

  // ==========================================================================
  // STATISTICS & REPORTING
  // ==========================================================================

  /**
   * Get audit statistics for a date range
   */
  static async getAuditStatistics(
    startDate: Date,
    endDate: Date
  ): Promise<AuditStatistics> {
    const q = query(
      collection(db, AUDIT_LOGS_COLLECTION),
      where('timestamp', '>=', Timestamp.fromDate(startDate)),
      where('timestamp', '<=', Timestamp.fromDate(endDate)),
      orderBy('timestamp', 'desc')
    );

    const snapshot = await getDocs(q);
    const logs = snapshot.docs.map(docSnap => this.docToAuditLog(docSnap.id, docSnap.data()));

    const stats: AuditStatistics = {
      totalLogs: logs.length,
      logsByAction: {},
      logsBySeverity: {},
      logsByEntityType: {},
      phiAccessCount: 0,
      failedActionsCount: 0,
      uniqueUsers: 0,
      uniqueIPs: 0,
      logsToday: 0,
      logsThisWeek: 0,
      logsThisMonth: 0,
      unauthorizedAccessAttempts: 0,
      suspiciousActivities: 0,
      emergencyAccessCount: 0,
    };

    const uniqueUserIds = new Set<string>();
    const uniqueIPs = new Set<string>();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    for (const log of logs) {
      // Count by action
      stats.logsByAction[log.action] = (stats.logsByAction[log.action] || 0) + 1;

      // Count by severity
      stats.logsBySeverity[log.severity] = (stats.logsBySeverity[log.severity] || 0) + 1;

      // Count by entity type
      if (log.entityType) {
        stats.logsByEntityType[log.entityType] = (stats.logsByEntityType[log.entityType] || 0) + 1;
      }

      // PHI access
      if (log.containsPHI) {
        stats.phiAccessCount++;
      }

      // Failed actions
      if (!log.success) {
        stats.failedActionsCount++;
      }

      // Unique tracking
      if (log.userId) {
        uniqueUserIds.add(log.userId);
      }
      if (log.ipAddress) {
        uniqueIPs.add(log.ipAddress);
      }

      // Time-based stats
      const logDate = new Date(log.timestamp);
      if (logDate >= today) {
        stats.logsToday++;
      }
      if (logDate >= weekAgo) {
        stats.logsThisWeek++;
      }
      if (logDate >= monthAgo) {
        stats.logsThisMonth++;
      }

      // Security stats
      if (log.action === AuditLogAction.unauthorizedAccess) {
        stats.unauthorizedAccessAttempts++;
      }
      if (log.action === AuditLogAction.suspiciousActivity) {
        stats.suspiciousActivities++;
      }
      if (log.accessReason === AccessReason.emergencyAccess) {
        stats.emergencyAccessCount++;
      }
    }

    stats.uniqueUsers = uniqueUserIds.size;
    stats.uniqueIPs = uniqueIPs.size;

    return stats;
  }

  /**
   * Get user activity summary
   */
  static async getUserActivitySummary(
    startDate: Date,
    endDate: Date
  ): Promise<AuditSummaryByUser[]> {
    const { logs } = await this.getAuditLogs({
      startDate,
      endDate,
    }, 10000);

    const userMap = new Map<string, AuditSummaryByUser>();

    for (const log of logs) {
      if (!log.userId) continue;

      let userSummary = userMap.get(log.userId);
      if (!userSummary) {
        userSummary = {
          userId: log.userId,
          userName: log.userName || 'Unknown',
          userEmail: log.userEmail || 'Unknown',
          totalActions: 0,
          phiAccessCount: 0,
          lastActivity: log.timestamp,
          actionBreakdown: {},
        };
        userMap.set(log.userId, userSummary);
      }

      userSummary.totalActions++;
      if (log.containsPHI) {
        userSummary.phiAccessCount++;
      }
      userSummary.actionBreakdown[log.action] = 
        (userSummary.actionBreakdown[log.action] || 0) + 1;

      if (log.timestamp > userSummary.lastActivity) {
        userSummary.lastActivity = log.timestamp;
      }
    }

    return Array.from(userMap.values()).sort((a, b) => 
      b.lastActivity.getTime() - a.lastActivity.getTime()
    );
  }

  /**
   * Generate PHI access report for a patient
   */
  static async generatePHIAccessReport(
    patientId: string,
    startDate: Date,
    endDate: Date
  ): Promise<PHIAccessReport> {
    const logs = await this.getPHIAccessLogs(10000, { patientId, startDate, endDate });

    const accessByReason: Record<string, number> = {};
    const uniqueAccessors = new Set<string>();

    for (const log of logs) {
      if (log.accessReason) {
        accessByReason[log.accessReason] = (accessByReason[log.accessReason] || 0) + 1;
      }
      if (log.userId) {
        uniqueAccessors.add(log.userId);
      }
    }

    // Log this report generation
    await this.writeLog({
      action: AuditLogAction.auditReportGenerated,
      severity: AuditLogSeverity.info,
      description: `PHI access report generated for patient ${patientId}`,
      entityType: AuditEntityType.report,
      patientId,
      containsPHI: true,
      accessReason: AccessReason.auditReview,
      details: {
        dateRange: { start: startDate.toISOString(), end: endDate.toISOString() },
        accessCount: logs.length,
      },
    });

    return {
      patientId,
      accessLogs: logs,
      accessCount: logs.length,
      uniqueAccessors: uniqueAccessors.size,
      accessByReason,
      dateRange: { start: startDate, end: endDate },
    };
  }

  /**
   * Generate HIPAA compliance report
   */
  static async generateHIPAAComplianceReport(
    startDate: Date,
    endDate: Date
  ): Promise<HIPAAComplianceReport> {
    const [stats, userActivity, securityIncidents, emergencyLogs] = await Promise.all([
      this.getAuditStatistics(startDate, endDate),
      this.getUserActivitySummary(startDate, endDate),
      this.getSecurityEvents(500),
      this.getEmergencyAccessLogs(500),
    ]);

    // Filter security incidents to date range
    const filteredSecurityIncidents = securityIncidents.filter(
      log => log.timestamp >= startDate && log.timestamp <= endDate
    );

    const unauthorizedAccessAttempts = filteredSecurityIncidents.filter(
      log => log.action === AuditLogAction.unauthorizedAccess
    );

    const filteredEmergencyLogs = emergencyLogs.filter(
      log => log.timestamp >= startDate && log.timestamp <= endDate
    );

    // Verify integrity
    const verifyIntegrity = httpsCallable<
      { startDate?: string; endDate?: string; limit?: number },
      { success: boolean; isValid: boolean; verifiedCount: number }
    >(functions, 'verifyAuditLogIntegrity');

    let integrityStatus: 'verified' | 'tampered' | 'unknown' = 'unknown';
    try {
      const integrityResult = await verifyIntegrity({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        limit: 1000,
      });
      integrityStatus = integrityResult.data.isValid ? 'verified' : 'tampered';
    } catch (error) {
      console.error('Error verifying integrity:', error);
    }

    const report: HIPAAComplianceReport = {
      reportId: `hipaa_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      generatedAt: new Date(),
      generatedBy: 'system', // Will be updated with actual user
      dateRange: { start: startDate, end: endDate },
      
      totalPHIAccesses: stats.phiAccessCount,
      phiAccessByType: {} as Record<PHIDataType, number>,
      phiAccessByReason: {} as Record<AccessReason, number>,
      
      userActivity,
      
      securityIncidents: filteredSecurityIncidents,
      unauthorizedAccessAttempts,
      
      emergencyAccessLogs: filteredEmergencyLogs,
      emergencyAccessCount: filteredEmergencyLogs.length,
      
      logIntegrityStatus: integrityStatus,
      integrityCheckTimestamp: new Date(),
    };

    // Log the report generation
    await this.writeLog({
      action: AuditLogAction.auditReportGenerated,
      severity: AuditLogSeverity.info,
      description: 'HIPAA compliance report generated',
      entityType: AuditEntityType.report,
      entityId: report.reportId,
      entityName: 'HIPAA Compliance Report',
      containsPHI: false,
      details: {
        dateRange: { start: startDate.toISOString(), end: endDate.toISOString() },
        totalPHIAccesses: stats.phiAccessCount,
        securityIncidentCount: filteredSecurityIncidents.length,
        emergencyAccessCount: filteredEmergencyLogs.length,
      },
    });

    return report;
  }

  // ==========================================================================
  // HELPER METHODS
  // ==========================================================================

  /**
   * Convert Firestore document to AuditLog
   */
  private static docToAuditLog(id: string, data: any): AuditLog {
    return {
      id,
      action: data.action || AuditLogAction.other,
      severity: data.severity || AuditLogSeverity.info,
      description: data.description || '',
      
      userId: data.userId,
      userEmail: data.userEmail,
      userName: data.userName,
      userRole: data.userRole,
      
      entityType: data.entityType,
      entityId: data.entityId,
      entityName: data.entityName,
      
      sessionId: data.sessionId,
      requestId: data.requestId,
      
      ipAddress: data.ipAddress,
      userAgent: data.userAgent,
      geoLocation: data.geoLocation,
      
      containsPHI: data.containsPHI || false,
      phiDataTypes: data.phiDataTypes,
      accessReason: data.accessReason,
      accessJustification: data.accessJustification,
      patientId: data.patientId,
      
      changeDetails: data.changeDetails,
      
      success: data.success !== false,
      errorMessage: data.errorMessage,
      errorCode: data.errorCode,
      
      details: data.details,
      metadata: data.metadata,
      tags: data.tags,
      
      retentionPeriodDays: data.retentionPeriodDays,
      isArchived: data.isArchived,
      archivedAt: data.archivedAt?.toDate(),
      
      hash: data.hash,
      previousLogHash: data.previousLogHash,
      
      timestamp: data.timestamp?.toDate() || new Date(),
      serverTimestamp: data.serverTimestamp?.toDate(),
    };
  }
}

// Export convenience functions for common operations
export const logLogin = AuditLogService.logLogin.bind(AuditLogService);
export const logLogout = AuditLogService.logLogout.bind(AuditLogService);
export const logPHIAccess = AuditLogService.logPHIAccess.bind(AuditLogService);
export const logDataExport = AuditLogService.logDataExport.bind(AuditLogService);
export const logUnauthorizedAccess = AuditLogService.logUnauthorizedAccess.bind(AuditLogService);
export const logEntityOperation = AuditLogService.logEntityOperation.bind(AuditLogService);
